import { GroupSecurity } from '@quromedical/fhir-common';
import type { PatientRouteParams } from 'app/routes';
import { PatientHeaderSection } from 'core/patient';
import { Section, SkeletonProvider, Tabs } from 'design-system';
import { useGlobalSearchParams } from 'expo-router';
import { useRevalidation } from 'hooks/useRevalidation';
import { useUserSession } from 'hooks/useUserSession';
import {
  CoverageApi,
  PatientApi,
  PatientEndpoint,
} from 'integration/aggregate';
import type { PatientDrawerScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import { strings } from 'strings';
import useSWR from 'swr';

import {
  PatientDevicesTab,
  PatientMedicalAidTab,
  PatientMedicalAidUpdateTab,
  PatientNextOfKinTab,
  PatientProfileTab,
} from './tabs';

const api = new PatientApi();
const coverageApi = new CoverageApi();

export const PatientProfileScreen: React.FC<
  PatientDrawerScreenProps<'Profile'>
> = () => {
  const params = useGlobalSearchParams<PatientRouteParams>();
  const patientId = params.patientId;

  const session = useUserSession();

  const patientFetcher = useCallback(() => {
    if (!patientId) {
      return undefined;
    }

    return api.getPatient(patientId);
  }, [patientId]);
  const patientKey = api.getPatientSWRKey(patientId, PatientEndpoint.Base);

  const coverageFetcher = useCallback(() => {
    if (!patientId) {
      return undefined;
    }

    return coverageApi.getCoverage(patientId);
  }, [patientId]);

  const coverageKey = api.getPatientSWRKey(patientId, PatientEndpoint.Coverage);

  const { data } = useSWR(patientKey, patientFetcher);
  const coverageResult = useSWR(coverageKey, coverageFetcher);

  const revalidate = useRevalidation(patientKey);

  if (!patientId) {
    return <NotFoundScreen />;
  }

  const patientFilePermissions = [GroupSecurity.PatientRead];
  const hasPatientFilePermissions = session.hasAny(patientFilePermissions);
  const deviceManagePermissions = [
    GroupSecurity.DeviceUseStatementCreate,
    GroupSecurity.DeviceUseStatementUpdate,
  ];
  const hasDeviceManagePermissions = session.hasAny(deviceManagePermissions);
  const hasDeviceUseStatementCreatePermission = session.hasAny([
    GroupSecurity.DeviceUseStatementCreate,
  ]);

  const hasNextOfKinPermissions = session.hasAny([
    GroupSecurity.RelatedPersonRead,
  ]);

  const hasCoverage = !!coverageResult.data?.coverage?.memberNumber;

  const canViewCoverage =
    hasCoverage && session.hasAny([GroupSecurity.CoverageRead]);
  const canCreateCoverage =
    !hasCoverage && session.hasAny([GroupSecurity.CoverageCreate]);

  return (
    <ScrollView>
      <SkeletonProvider loading={!data}>
        <Section>
          <PatientHeaderSection id={patientId} />

          <Tabs.Group>
            <Tabs.Screen
              isHidden={!hasPatientFilePermissions}
              title={strings.LabelPatientFile}
            >
              <PatientProfileTab
                patientId={patientId}
                patient={data}
                coverage={coverageResult.data}
                isValidating={!data}
              />
            </Tabs.Screen>

            <Tabs.Screen
              isHidden={!hasNextOfKinPermissions}
              title={strings.LabelNextOfKin}
            >
              <PatientNextOfKinTab patientId={patientId} />
            </Tabs.Screen>

            <Tabs.Screen
              isHidden={!canViewCoverage}
              title={strings.FormLabelMedicalAid}
            >
              <PatientMedicalAidTab patientId={patientId} />
            </Tabs.Screen>

            <Tabs.Screen
              isHidden={!canCreateCoverage}
              title={strings.FormLabelMedicalAid}
            >
              {data ? (
                <PatientMedicalAidUpdateTab
                  patient={data}
                  patientId={patientId}
                  revalidate={revalidate}
                />
              ) : null}
            </Tabs.Screen>

            <Tabs.Screen
              isHidden={!hasDeviceManagePermissions}
              title={strings.LabelDevices}
            >
              <PatientDevicesTab
                patientId={patientId}
                canCreateDeviceUseStatement={
                  hasDeviceUseStatementCreatePermission
                }
              />
            </Tabs.Screen>
          </Tabs.Group>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
